/**
 * 「よく使われる事例」1 件のカード。
 *
 * ## 表示順が仕様である
 *
 * **課題 → 対策 → 利用 SSML → 期待効果 → 関連構文** の順に固定する。
 * 利用者は「今困っていること」と一致するかを最初に判断するため、
 * 課題を先頭に置く。SSML を先に見せると、自分の課題に効くのか分からないまま
 * コピーされてしまう (効かない設定を入れて「SSML は効果がない」と
 * 判断される事故につながる)。
 *
 * ## 設計上の約束
 *
 * - **データは props で受ける** (`useCases.ts` を import しない)。
 *   Phase2 で API 化しても本コンポーネントは無変更で使える (R14)。
 * - 操作要素は置かない (Readme は読むだけ)。ただし SSML は
 *   コピーして編集欄へ貼るため `<pre>` で改行と空白を保持する。
 * - `confidence: 'trial'` の事例は **見出し横に注記を出す**。
 *   「入れれば直る」と誤解されると、効かなかったときに Readme 全体の
 *   信頼が落ちるため、期待値は事例単位で明示する。
 */
import type React from 'react';

import type { SsmlUseCase } from './types';
import { ssmlUseCaseTagLabel } from './useCaseTags';

export interface UseCaseCardProps {
  /** 表示する事例 */
  useCase: SsmlUseCase;
  /**
   * 「関連構文」が押されたときの通知。
   *
   * 省略時は関連構文を **文字列として表示するだけ** にする
   * (構文辞典へ遷移できない文脈でも本コンポーネントを使えるようにする)。
   */
  onSelectRelatedSyntax?: (category: SsmlUseCase['relatedSyntax'][number]['category']) => void;
}

export function UseCaseCard({
  useCase,
  onSelectRelatedSyntax,
}: UseCaseCardProps): React.JSX.Element {
  const isTrial = useCase.confidence === 'trial';

  return (
    <li className="ssml-usecase-card" data-testid="ssml-usecase-card" id={`usecase-${useCase.id}`}>
      <div className="ssml-usecase-card__head">
        <h4 className="ssml-usecase-card__title">{useCase.title}</h4>
        {/*
          確度の明示。trial は「改善を試みる方法」であることを見出し位置で伝える
          (本文を読み飛ばしても誤解しないようにする)。
        */}
        <span
          className={`ssml-usecase-card__confidence${isTrial ? ' is-trial' : ''}`}
          data-testid={`ssml-usecase-confidence-${useCase.id}`}
        >
          {isTrial ? '改善を試みる方法' : '定番の対策'}
        </span>
      </div>

      {/* タグは絞り込みの軸であり、事例の性格を一目で示すラベルにもなる */}
      <ul className="ssml-usecase-card__tags">
        {useCase.tags.map((tag) => (
          <li
            key={tag}
            className="ssml-usecase-card__tag"
            data-testid={`ssml-usecase-card-tag-${tag}`}
          >
            {ssmlUseCaseTagLabel(tag)}
          </li>
        ))}
      </ul>

      <dl className="ssml-usecase-card__fields">
        <dt>課題</dt>
        <dd>{useCase.problem}</dd>

        <dt>対策</dt>
        <dd>{useCase.countermeasure}</dd>

        <dt>利用 SSML</dt>
        <dd>
          <ul className="ssml-usecase-card__examples">
            {useCase.examples.map((example) => (
              <li key={example.label} className="ssml-usecase-card__example">
                <p className="ssml-usecase-card__example-label">{example.label}</p>
                {/* コピーして SSML 編集欄へ貼れるよう改行と空白を保持する */}
                <pre className="ssml-usecase-card__ssml">{example.ssml}</pre>
                {example.note ? (
                  <p className="ssml-usecase-card__example-note">{example.note}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </dd>

        <dt>期待効果</dt>
        <dd>{useCase.expectedEffect}</dd>

        <dt>関連構文</dt>
        <dd>
          {useCase.relatedSyntax.length === 0 ? (
            <span className="ssml-usecase-card__related-empty">なし</span>
          ) : (
            <ul className="ssml-usecase-card__related">
              {useCase.relatedSyntax.map((related) => (
                <li key={`${related.category}-${related.title}`}>
                  {onSelectRelatedSyntax ? (
                    /*
                      構文辞典の該当カテゴリへ切り替える。
                      事例で興味を持った直後に詳細へ辿れないと、
                      利用者は結局タグ名で検索し直すことになる。
                    */
                    <button
                      type="button"
                      className="ssml-usecase-card__related-link"
                      data-testid={`ssml-usecase-related-${useCase.id}-${related.category}`}
                      onClick={() => onSelectRelatedSyntax(related.category)}
                    >
                      {related.title}
                    </button>
                  ) : (
                    related.title
                  )}
                </li>
              ))}
            </ul>
          )}
        </dd>
      </dl>
    </li>
  );
}
